import Box from '@mui/material/Box';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { ReactNode, useEffect, useState } from 'react';

import { EmptyStateMessage } from '~components/Common/EmptyStateMessage';
import { subscribeToRequestFailures } from '~services/connectionModule';
import { getGoneJobIdFromFailedRequest } from '~utils/jobs/getJobIdFromJobDetailsUrl';

interface JobAvailabilityGuardProps {
  children: ReactNode;
  jobId: string;
}

const isGuardedJobGone = (error: unknown, jobId: string) => {
  if (!isAxiosError(error)) {
    return false;
  }

  const goneJobId = getGoneJobIdFromFailedRequest({
    method: error.config?.method,
    status: error.response?.status,
    url: error.config?.url,
  });

  return goneJobId === jobId;
};

export default function JobAvailabilityGuard({
  children,
  jobId,
}: JobAvailabilityGuardProps) {
  const [noLongerAvailableJobId, setNoLongerAvailableJobId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    const isRequestFailureShownByView = (error: unknown) => {
      const isJobNoLongerAvailable = isGuardedJobGone(error, jobId);

      if (isJobNoLongerAvailable) {
        setNoLongerAvailableJobId(jobId);
      }

      return isJobNoLongerAvailable;
    };

    return subscribeToRequestFailures(isRequestFailureShownByView);
  }, [jobId]);

  if (noLongerAvailableJobId === jobId) {
    return (
      <Box sx={{ p: 2 }}>
        <EmptyStateMessage message={t('dispatch.job.no_longer_available')} />
      </Box>
    );
  }

  return <>{children}</>;
}
